import { Injectable, inject, signal } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { HelperNavControllerService, NavPathType } from './helper-nav-controller.service';
import { HelperDebugLoggerControllerService } from './helper-debug-logger-controller.service';
import { ROUTE_DIRECTION } from '../types/route.type';

export interface ParsedDeepLink {
  rawUrl: string;
  scheme: string;
  host: string;
  path: string;
  queryParams: Record<string, string>;
  targetRoute?: string;
}

@Injectable({
  providedIn: 'root',
})
export class HelperDeepLinkControllerService {
  private navService = inject(HelperNavControllerService);
  private debugLogger = inject(HelperDebugLoggerControllerService);

  public readonly isInitialized = signal<boolean>(false);

  constructor() {
    this.init();
  }

  /**
   * Initializes deep link listeners on native and web platforms
   */
  public init(): void {
    if (this.isInitialized()) {
      return;
    }

    if (Capacitor.isNativePlatform()) {
      App.addListener('appUrlOpen', (event) => {
        this.handleIncomingUrl(event.url);
      });
    } else if (typeof window !== 'undefined') {
      // Parse initial web launch URL parameters
      this.handleIncomingUrl(window.location.href);
    }

    this.isInitialized.set(true);
  }

  /**
   * Parses an incoming deep link URL and triggers navigation/referral attribution
   */
  public handleIncomingUrl(urlStr: string): ParsedDeepLink | null {
    if (!urlStr) {
      return null;
    }

    try {
      const parsed = this.parseUrl(urlStr);

      this.debugLogger.addLog('info', `[DEEP_LINK] Parsed URL: ${parsed.rawUrl}`, {
        path: parsed.path,
        params: parsed.queryParams,
      });

      // Navigate to target route if valid
      if (parsed.targetRoute && parsed.targetRoute !== '/' && parsed.targetRoute !== '') {
        const cleanRoute = parsed.targetRoute.replace(/^\//, '') as NavPathType;
        this.navService.navigate(cleanRoute, ROUTE_DIRECTION.Forward);
      }

      return parsed;
    } catch (error) {
      this.debugLogger.addLog('error', `[DEEP_LINK] Failed to parse: ${urlStr}`, error);
      return null;
    }
  }

  private parseUrl(urlStr: string): ParsedDeepLink {
    const queryParams: Record<string, string> = {};
    let scheme = 'https';
    let host = '';
    let path = '/';

    try {
      const url = new URL(urlStr);
      scheme = url.protocol.replace(':', '');
      host = url.host;
      if (!scheme.startsWith('http') && host) {
        path = `/${host}${url.pathname === '/' ? '' : url.pathname}`;
      } else {
        path = url.pathname;
      }
      url.searchParams.forEach((val, key) => {
        queryParams[key] = val;
      });
    } catch {
      // Fallback for custom schemes like bm://chats or bm://story/123
      const parts = urlStr.split('://');
      if (parts.length > 1) {
        scheme = parts[0] || 'custom';
        const rest = parts[1] || '';
        const [pathPart, queryPart] = rest.split('?');
        path = `/${pathPart || ''}`;
        if (queryPart) {
          const searchParams = new URLSearchParams(queryPart);
          searchParams.forEach((val, key) => {
            queryParams[key] = val;
          });
        }
      }
    }

    return {
      rawUrl: urlStr,
      scheme,
      host,
      path,
      queryParams,
      targetRoute: path !== '/' ? path : undefined,
    };
  }
}
